"use client";

import type { Dispatch, SetStateAction } from "react";
import clsx from "clsx";
import { Button, Select, Textarea } from "@/ui";
import { useOnboarding } from "@/lib/onboarding/OnboardingContext";
import SettingsField from "./SettingsField";
import styles from "./Ai.module.css";

const MAX_PROMPT_LENGTH = 3000;
const PROMPT_NEAR_LIMIT = 2800;

const CV_IMPORT_MODELS = [
  { value: "default", label: "Higher quality, slower" },
  { value: "fast", label: "Faster, less thorough" },
];

/**
 * The Settings modal owns the whole recruiter payload, so this panel only
 * declares the fields it touches.
 */
type RecruiterSettings = Record<string, unknown> & {
  cv_import_model?: string;
  user_base_prompt?: string;
};

export interface AiProps {
  recruiter: RecruiterSettings;
  setRecruiter: Dispatch<SetStateAction<RecruiterSettings>>;
  /**
   * Closes the Settings dialog. Redoing onboarding reports its progress in the
   * dashboard status bar, which sits behind this dialog.
   */
  onClose: () => void;
}

export default function Ai({ recruiter, setRecruiter, onClose }: AiProps) {
  const basePrompt = recruiter.user_base_prompt || "";
  const { openDomainPrompt } = useOnboarding();

  // The domain prompt is mounted by the dashboard layout, so closing Settings
  // reveals it rather than taking it down. Nothing runs until it's confirmed.
  const handleRedoOnboarding = () => {
    onClose();
    openDomainPrompt();
  };

  return (
    <>
      <SettingsField
        label="CV Import Model"
        description="Choose the AI model used when importing CVs. Higher quality takes longer; the faster option trades some thoroughness for speed."
      >
        <Select
          options={CV_IMPORT_MODELS}
          value={recruiter.cv_import_model || "default"}
          onChange={(value) =>
            setRecruiter((prev) => ({ ...prev, cv_import_model: value }))
          }
        />
      </SettingsField>

      <SettingsField
        label="AI Base Prompt"
        description="Customize how the SOLA AI assistant communicates with you and generates outreach messages. Add your personal style preferences and instructions here."
        htmlFor="settings-user-base-prompt"
      >
        <Textarea
          id="settings-user-base-prompt"
          rows={6}
          maxLength={MAX_PROMPT_LENGTH}
          placeholder="Example: Be concise and professional. Always use bullet points when presenting options..."
          value={basePrompt}
          onChange={(value) =>
            setRecruiter((prev) => ({
              ...prev,
              user_base_prompt: value.slice(0, MAX_PROMPT_LENGTH),
            }))
          }
        />
        <p
          className={clsx(
            styles.counter,
            basePrompt.length > PROMPT_NEAR_LIMIT && styles.counterNearLimit,
          )}
        >
          {basePrompt.length}/{MAX_PROMPT_LENGTH} characters
        </p>
      </SettingsField>

      <SettingsField
        label="Automated Onboarding"
        description="Research your company again and suggest fresh branding, style and outreach defaults. You review the findings before anything is applied."
      >
        <Button variant="secondary" onClick={handleRedoOnboarding}>
          Run onboarding again
        </Button>
      </SettingsField>
    </>
  );
}
